import { AIIdeaInput, BusinessIdea } from '../../types/ai';

/**
 * Prompt Builder Service
 * Handles all AI prompt generation logic
 */
export class PromptBuilder {
  /**
   * Get language name from language code
   */
  private static getLanguageName(languageCode: string): string {
    const languageMap: Record<string, string> = {
      'en': 'English',
      'zh': 'Chinese (中文)',
      'es': 'Spanish (Español)',
      'fr': 'French (Français)',
      'de': 'German (Deutsch)',
      'ja': 'Japanese (日本語)',
      'ko': 'Korean (한국어)',
      'pt': 'Portuguese (Português)',
      'ru': 'Russian (Русский)',
      'ar': 'Arabic (العربية)',
      'hi': 'Hindi (हिन्दी)',
      'it': 'Italian (Italiano)'
    };
    return languageMap[languageCode] || 'English';
  }

  /**
   * Create prompt for idea generation with randomization
   */
  static createIdeaGenerationPrompt(
    input: AIIdeaInput, 
    language: string = 'en',
    targetMarket: string = 'Global',
    detailLevel: 'basic' | 'standard' | 'detailed' = 'standard'
  ): string {
    const randomSeed = Math.floor(Math.random() * 10000);
    const currentTime = new Date().toISOString();
    
    const focusAreas = [
      'efficiency and productivity',
      'cost reduction and optimization', 
      'user experience improvement',
      'data insights and analytics',
      'workflow automation',
      'communication and collaboration'
    ];
    
    const randomFocus = focusAreas[Math.floor(Math.random() * focusAreas.length)];
    
    const languageInstruction = language !== 'en' 
      ? `\n\nIMPORTANT: Generate ALL business ideas in ${this.getLanguageName(language)} language. All fields including title, problem, solution, and descriptions must be in ${this.getLanguageName(language)}.`
      : '';
    
    const marketInstruction = targetMarket !== 'Global'
      ? `\n\nTarget Market: ${targetMarket}. Focus on problems, opportunities, and solutions specific to the ${targetMarket} market. Consider local regulations, culture, market size, and unique challenges in ${targetMarket}.`
      : '\n\nTarget Market: Global. Consider ideas that can work internationally or be easily adapted to different markets.';
    
    const detailInstructions = {
      'basic': 'Keep descriptions concise and focused on core concepts.',
      'standard': 'Provide balanced detail covering key aspects of each idea.',
      'detailed': 'Provide comprehensive detail including market analysis, competitive landscape, potential challenges, and implementation considerations.'
    };
    
    const problemDetail = {
      'basic': 'Describe the core problem in 2-3 sentences',
      'standard': 'Describe the problem with context: who experiences it, when it occurs, current workarounds, and why it matters (4-6 sentences)',
      'detailed': 'Provide comprehensive problem analysis: who experiences it, frequency/severity, current inadequate solutions, business/personal impact, cost of not solving it, and specific pain points (7-10 sentences)'
    };
    
    const solutionDetail = {
      'basic': 'Describe the core solution approach in 2-3 sentences',
      'standard': 'Describe the solution with key features: main approach, 2-3 core features, how it solves the problem, and what makes it different (4-6 sentences)',
      'detailed': 'Provide comprehensive solution details: approach and methodology, 3-5 key features with benefits, unique value proposition, how it addresses each pain point, technology/implementation approach, and competitive advantages (7-10 sentences)'
    };
    
    return `You are an innovative startup advisor generating fresh business ideas. Current session: ${randomSeed} at ${currentTime}${languageInstruction}${marketInstruction}

User Profile:
- Skills: ${input.skills.join(', ')}
- Interests: ${input.interests.join(', ')}
- Problem Type Focus: ${input.problemType}

Generate exactly 3 UNIQUE micro-SaaS business ideas focusing on ${randomFocus} that:
1. Leverage their ${input.skills.join(' and ')} skills
2. Align with their interest in ${input.interests.join(' and ')}
3. Address ${input.problemType} problems in creative ways
4. Can be built by 1-2 people within 6 months
5. Have recurring revenue potential ($10-100/month per user)

${detailInstructions[detailLevel]}

Think outside the box and avoid generic solutions. Consider emerging trends, niche markets, and underserved audiences.

For each idea, provide:
- Title (concise, memorable)
- Problem: ${problemDetail[detailLevel]}
- Solution: ${solutionDetail[detailLevel]}
- Target Customer (specific demographic/industry)
- Validation Question (to test real demand)
- Skills Required (top 2-3 specific skills needed to build/deliver this solution)
- Technical Feasibility (High/Medium/Low with reasoning)
- Estimated Development (realistic timeline)

Format as JSON array with this structure:
[
  {
    "title": "Innovative Idea Title",
    "problem": "${problemDetail[detailLevel]} - Include who, when, why it matters, and impact",
    "solution": "${solutionDetail[detailLevel]} - Include approach, features, benefits, and differentiation",
    "targetCustomer": "Specific audience with demographics",
    "validationQuestion": "Actionable question to validate demand",
    "skillsRequired": ["Primary skill", "Secondary skill"],
    "technicalFeasibility": "High|Medium|Low",
    "estimatedDevelopment": "Realistic timeline (e.g., '3-4 weeks for MVP')"
  }
]

Generate completely new ideas - avoid common solutions. Be creative and specific. Only return the JSON array.`;
  }

  /**
   * Create a shorter prompt for idea generation when hitting token limits
   */
  static createShortIdeaGenerationPrompt(
    input: AIIdeaInput, 
    language: string = 'en',
    targetMarket: string = 'Global',
    detailLevel: 'basic' | 'standard' | 'detailed' = 'standard'
  ): string {
    const randomSeed = Math.floor(Math.random() * 10000);
    
    const languageInstruction = language !== 'en' 
      ? ` Generate in ${this.getLanguageName(language)} language.`
      : '';
    
    const marketInstruction = targetMarket !== 'Global'
      ? ` Target market: ${targetMarket}.`
      : '';
    
    return `Generate 3 unique micro-SaaS business ideas. Session: ${randomSeed}${languageInstruction}${marketInstruction}

Skills: ${input.skills.join(', ')}
Interests: ${input.interests.join(', ')}
Problem Focus: ${input.problemType}

Requirements:
- Leverage user skills
- Match interests  
- Address ${input.problemType} problems
- Buildable by 1-2 people in 6 months
- $10-100/month revenue potential

Format as JSON array:
[
  {
    "title": "Idea Title",
    "problem": "Problem description",
    "solution": "Solution approach", 
    "targetCustomer": "Target audience",
    "validationQuestion": "Validation question",
    "skillsRequired": ["Skill 1", "Skill 2"],
    "technicalFeasibility": "High|Medium|Low",
    "estimatedDevelopment": "Timeline"
  }
]

Return only the JSON array.`;
  }

  /**
   * Create enhanced prompt for business kit generation
   * Includes revenue model, market analysis, resource requirements, and launch guide
   */
  static createBusinessKitPrompt(
    input: AIIdeaInput,
    language: string = 'en',
    targetMarket: string = 'Global',
    detailLevel: 'basic' | 'standard' | 'detailed' = 'standard'
  ): string {
    const randomSeed = Math.floor(Math.random() * 10000);
    const currentTime = new Date().toISOString();
    
    const focusAreas = [
      'efficiency and productivity',
      'cost reduction and optimization', 
      'user experience improvement',
      'data insights and analytics',
      'workflow automation',
      'communication and collaboration'
    ];
    
    const randomFocus = focusAreas[Math.floor(Math.random() * focusAreas.length)];
    
    const languageInstruction = language !== 'en' 
      ? `\n\nIMPORTANT: Generate ALL content in ${this.getLanguageName(language)} language. Every field including title, problem, solution, revenue strategies, market analysis, resource descriptions, and launch guide must be in ${this.getLanguageName(language)}.`
      : '';
    
    const marketInstruction = targetMarket !== 'Global'
      ? `\n\nTarget Market: ${targetMarket}. Focus on problems, opportunities, and solutions specific to the ${targetMarket} market. Consider local regulations, culture, market size, competitors, and unique challenges in ${targetMarket}. Tailor revenue models and market analysis to ${targetMarket} context.`
      : '\n\nTarget Market: Global. Consider ideas that can work internationally or be easily adapted to different markets.';
    
    const detailInstructions = {
      'basic': 'Keep descriptions concise and focused on core concepts. Provide essential information only.',
      'standard': 'Provide balanced detail covering key aspects of each idea with practical examples.',
      'detailed': 'Provide comprehensive detail including in-depth market analysis, competitive landscape, potential challenges, implementation considerations, and strategic recommendations.'
    };
    
    const problemDetailLevel = {
      'basic': 'Describe the core problem in 1-2 sentences',
      'standard': 'Describe the problem with context: who experiences it, when it occurs, current workarounds, and why it matters (2-3 sentences)',
      'detailed': 'Provide comprehensive problem analysis: who experiences it, frequency/severity, current inadequate solutions, business/personal impact, cost of not solving it, and specific pain points (3-5 sentences)'
    };
    
    const solutionDetailLevel = {
      'basic': 'Describe the core solution approach in 1-2 sentences',
      'standard': 'Describe the solution with key features: main approach, 2-3 core features, how it solves the problem, and what makes it different (2-3 sentences)',
      'detailed': 'Provide comprehensive solution details: approach and methodology, 3-5 key features with benefits, unique value proposition, how it addresses each pain point, technology/implementation approach, and competitive advantages (4-6 sentences)'
    };
    
    const revenueDetailLevel = {
      'basic': '2 monetization strategies with brief pricing examples',
      'standard': '2-3 monetization strategies with specific pricing examples and target segments',
      'detailed': '3 monetization strategies with detailed pricing tiers, target segments, and revenue projections'
    };
    
    const marketDetailLevel = {
      'basic': 'Market size estimate and 2 key competitors',
      'standard': 'Market size with growth rate, 2-3 competitors with strengths/weaknesses, and 2 opportunity gaps',
      'detailed': 'Detailed market size with trends, 3 competitors with comprehensive analysis, and 3 differentiation opportunities'
    };
    
    const resourceDetailLevel = {
      'basic': '2-3 key skills and 2-3 essential tools with basic cost estimate',
      'standard': '3-4 skills with proficiency levels, 3-4 tools with alternatives, and detailed cost breakdown',
      'detailed': '4-5 skills with learning paths, 4-5 tools with comprehensive alternatives, and detailed cost analysis with ROI considerations'
    };
    
    const launchDetailLevel = {
      'basic': '5 phases with key milestones',
      'standard': '5-7 phases with milestones and specific actions',
      'detailed': '7 phases with detailed milestones, comprehensive actions, and success metrics'
    };

    return `You are an innovative startup advisor generating comprehensive business idea kits. Current session: ${randomSeed} at ${currentTime}${languageInstruction}${marketInstruction}

User Profile:
- Skills: ${input.skills.join(', ')}
- Interests: ${input.interests.join(', ')}
- Problem Type Focus: ${input.problemType}

Generate exactly 3 UNIQUE micro-SaaS business ideas focusing on ${randomFocus} that:
1. Leverage their ${input.skills.join(' and ')} skills
2. Align with their interest in ${input.interests.join(' and ')}
3. Address ${input.problemType} problems in creative ways
4. Can be built by 1-2 people within 6 months
5. Have recurring revenue potential ($10-100/month per user)

${detailInstructions[detailLevel]}

Think outside the box and avoid generic solutions. Consider emerging trends, niche markets, and underserved audiences.

For each idea, provide a COMPLETE BUSINESS KIT with:

**Core Idea:**
- Title (concise, memorable)
- Problem: ${problemDetailLevel[detailLevel]}
- Solution: ${solutionDetailLevel[detailLevel]}
- Target Customer (specific demographic/industry)
- Validation Question (to test real demand)
- red (top 2-3 specific skills needed)
- Technical Feasibility (high/medium/low with reasoning)
- Estimated Development (realistic timeline)

**Revenue Model:**
Generate ${revenueDetailLevel[detailLevel]}. For each strategy include:
- Type (e.g., "Subscription", "Freemium", "Usage-based", "One-time purchase", "Tiered pricing")
- Description (how it works)
- Pricing Example (specific numbers, e.g., "$29/month for Pro plan")
- Target Segment (who this pricing targets)
- Estimated Revenue (optional, e.g., "$5K-15K MRR at 100 customers")

**Market Analysis:**
Provide ${marketDetailLevel[detailLevel]}:
- Market Size (with growth rate if available, e.g., "$2.5B global market, growing 15% annually")
- Competitors (${detailLevel === 'basic' ? '2' : detailLevel === 'standard' ? '2-3' : '3'} existing solutions):
  - Name
  - Strengths (2-3 key advantages)
  - Weaknesses (2-3 limitations or gaps)
- Opportunity Gaps (${detailLevel === 'basic' ? '2' : detailLevel === 'standard' ? '2' : '3'} differentiation opportunities)

**Resource Requirements:**
Detail ${resourceDetailLevel[detailLevel]}:
- Skills (with proficiency level: beginner/intermediate/advanced, and whether it can be learned vs. must hire)
- Tools (name, purpose, cost, and 1-2 low-cost alternatives)
- Estimated Cost:
  - Minimum (e.g., "$0-500")
  - Recommended (e.g., "$500-2000")
  - Breakdown (2-4 categories with amounts, e.g., "Hosting: $20/month", "Tools: $50/month")

**Launch Guide:**
Create ${launchDetailLevel[detailLevel]}:
- Total Timeline (e.g., "3-6 months to first revenue")
- Phases (${detailLevel === 'basic' ? '5' : detailLevel === 'standard' ? '5-7' : '7'} sequential steps):
  - Phase number
  - Name (e.g., "Validation", "MVP Development", "Beta Launch")
  - Duration (e.g., "2-3 weeks")
  - Milestones (2-3 key achievements)
  - Actions (3-5 specific tasks to complete)

Format as JSON array with this EXACT structure:
[
  {
    "title": "Innovative Idea Title",
    "problem": "${problemDetailLevel[detailLevel]} - Include who, when, why it matters, and impact",
    "solution": "${solutionDetailLevel[detailLevel]} - Include approach, features, benefits, and differentiation",
    "targetCustomer": "Specific audience with demographics",
    "validationQuestion": "Actionable question to validate demand",
    "skillsRequired": ["Primary skill", "Secondary skill"],
    "technicalFeasibility": "high",
    "estimatedDevelopment": "Realistic timeline (e.g., '3-4 weeks for MVP')",
    "revenueModel": {
      "strategies": [
        {
          "type": "Subscription",
          "description": "How this monetization works",
          "pricingExample": "$29/month for Pro plan",
          "targetSegment": "Small businesses with 5-20 employees"
        }
      ],
      "estimatedRevenue": "$5K-15K MRR at 100 customers"
    },
    "marketAnalysis": {
      "marketSize": "$2.5B global market, growing 15% annually",
      "competitors": [
        {
          "name": "Competitor Name",
          "strengths": ["Strength 1", "Strength 2"],
          "weaknesses": ["Weakness 1", "Weakness 2"]
        }
      ],
      "opportunityGaps": ["Gap 1", "Gap 2"]
    },
    "resourceRequirements": {
      "skills": [
        {
          "skill": "Skill name",
          "level": "intermediate",
          "canLearn": true
        }
      ],
      "tools": [
        {
          "name": "Tool name",
          "purpose": "What it's used for",
          "cost": "Free tier available",
          "alternatives": ["Alternative 1", "Alternative 2"]
        }
      ],
      "estimatedCost": {
        "minimum": "$0-500",
        "recommended": "$500-2000",
        "breakdown": [
          { "category": "Hosting", "amount": "$20/month" },
          { "category": "Tools", "amount": "$50/month" }
        ]
      }
    },
    "launchGuide": {
      "totalTimeline": "3-6 months to first revenue",
      "phases": [
        {
          "phase": 1,
          "name": "Validation",
          "duration": "2-3 weeks",
          "milestones": ["Milestone 1", "Milestone 2"],
          "actions": ["Action 1", "Action 2", "Action 3"]
        }
      ]
    }
  }
]

Generate completely new ideas - avoid common solutions. Be creative and specific. Ensure ALL enhancement fields are complete and actionable. Only return the JSON array.`;
  }

  /**
   * Create a shorter prompt for business kit generation when hitting token limits
   * Optimized for token efficiency while maintaining quality
   */
  static createShortBusinessKitPrompt(
    input: AIIdeaInput,
    language: string = 'en',
    targetMarket: string = 'Global',
    detailLevel: 'basic' | 'standard' | 'detailed' = 'standard'
  ): string {
    const randomSeed = Math.floor(Math.random() * 10000);
    
    const langCode = language !== 'en' ? ` Lang: ${this.getLanguageName(language)}.` : '';
    const market = targetMarket !== 'Global' ? ` Market: ${targetMarket}.` : '';
    
    // Adjust content depth based on detail level
    const contentDepth = {
      'basic': { strategies: 2, competitors: 2, gaps: 2, phases: 5, skills: 2, tools: 2 },
      'standard': { strategies: 2, competitors: 2, gaps: 2, phases: 5, skills: 3, tools: 3 },
      'detailed': { strategies: 3, competitors: 3, gaps: 3, phases: 7, skills: 4, tools: 4 }
    }[detailLevel];
    
    const problemInstr = {
      'basic': '1-2 sentences',
      'standard': '2-3 sentences with context, impact',
      'detailed': '3-5 sentences: who, frequency, current solutions, impact, pain points'
    }[detailLevel];
    
    const solutionInstr = {
      'basic': '1-2 sentences',
      'standard': '2-3 sentences with features, benefits',
      'detailed': '4-6 sentences: approach, 3-5 features, value prop, advantages'
    }[detailLevel];
    
    return `Generate 3 micro-SaaS kits. ID:${randomSeed}${langCode}${market}

Profile: ${input.skills.join(',')} | ${input.interests.join(',')} | ${input.problemType}

Criteria: Use skills, match interests, solve ${input.problemType}, 1-2 people, 6mo, $10-100/mo

Per idea:
- Core: title, problem (${problemInstr}), solution (${solutionInstr}), target, validation Q, skills[${contentDepth.skills}], feasibility, timeline
- Revenue: ${contentDepth.strategies} strategies (type, desc, price, segment), est revenue
- Market: size, ${contentDepth.competitors} competitors (name, strengths[2], weaknesses[2]), ${contentDepth.gaps} gaps
- Resources: skills[${contentDepth.skills}] (skill, level, canLearn), tools[${contentDepth.tools}] (name, purpose, cost, alts[2]), costs (min, rec, breakdown[2])
- Launch: ${contentDepth.phases} phases (phase#, name, duration, milestones[2], actions[3]), timeline

JSON:
[{
  "title":"","problem":"","solution":"","targetCustomer":"","validationQuestion":"",
  "skillsRequired":[""],"technicalFeasibility":"high","estimatedDevelopment":"",
  "revenueModel":{"strategies":[{"type":"","description":"","pricingExample":"","targetSegment":""}],"estimatedRevenue":""},
  "marketAnalysis":{"marketSize":"","competitors":[{"name":"","strengths":[""],"weaknesses":[""]}],"opportunityGaps":[""]},
  "resourceRequirements":{"skills":[{"skill":"","level":"intermediate","canLearn":true}],"tools":[{"name":"","purpose":"","cost":"","alternatives":[""]}],"estimatedCost":{"minimum":"","recommended":"","breakdown":[{"category":"","amount":""}]}},
  "launchGuide":{"totalTimeline":"","phases":[{"phase":1,"name":"","duration":"","milestones":[""],"actions":[""]}]}
}]`;
  }

  /**
   * Create prompt for validation questions
   */
  static createValidationQuestionsPrompt(idea: BusinessIdea, language: string = 'en'): string {
    const languageInstruction = language !== 'en' 
      ? `\n\nIMPORTANT: Generate ALL questions in ${this.getLanguageName(language)} language. All question text and options must be in ${this.getLanguageName(language)}.`
      : '';
    
    return `Create 4 validation survey questions for this business idea:${languageInstruction}

Title: ${idea.title}
Problem: ${idea.problem}
Solution: ${idea.solution}
Target Customer: ${idea.targetCustomer}

Generate 4 multiple-choice questions that test:
1. Problem frequency/severity
2. Current solutions usage
3. Willingness to try new solution
4. Pricing expectations

Format as JSON array:
[
  {
    "id": "q1",
    "text": "Question text",
    "type": "multiple-choice",
    "options": ["Option 1", "Option 2", "Option 3", "Option 4"],
    "required": true
  }
]

Only return the JSON array.`;
  }

  /**
   * Create prompt for task checklist
   */
  static createTaskChecklistPrompt(idea: BusinessIdea, language: string = 'en'): string {
    const languageInstruction = language !== 'en' 
      ? `\n\nIMPORTANT: Generate ALL tasks in ${this.getLanguageName(language)} language. All task titles and descriptions must be in ${this.getLanguageName(language)}.`
      : '';
    
    return `Create an MVP development checklist for this business idea:${languageInstruction}

Title: ${idea.title}
Solution: ${idea.solution}
Technical Feasibility: ${idea.technicalFeasibility}

Generate 8-12 specific, actionable tasks for building a minimal viable product. Focus on core functionality only.

Format as JSON array:
[
  {
    "id": "task1",
    "title": "Task title",
    "description": "Detailed description",
    "completed": false,
    "priority": "High|Medium|Low"
  }
]

Only return the JSON array.`;
  }
}
